package com.essntl.features.customfields.ui

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.contact.domain.model.ContactModel
import com.essntl.features.customfields.domain.model.CustomFieldsModel
import com.essntl.features.organization.domain.model.OrganizationModel
import com.essntl.features.supplier.domain.model.SupplierModel
import com.essntl.features.supplier.domain.repository.SupplierRepository
import com.essntl.features.travelagent.domain.model.TravelAgentModel
import com.essntl.features.travelagent.domain.repository.TravelAgentRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update

class CustomFieldsViewModel(
    private val state: MutableStateFlow<CustomFieldsState>,
    private val categoryRepository: CategoryRepository,
    private val areaRepository: AreaRepository,
    private val travelAgentRepository: TravelAgentRepository,
    private val supplierRepository: SupplierRepository,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    fun onEvent(event: CustomFieldsEvent) {
        when (event) {
            is CustomFieldsEvent.SetType -> setType(event)
            is CustomFieldsEvent.GetTravelAgent -> getTravelAgent(event.contactId)
            is CustomFieldsEvent.UpdateCustomFields -> updateCustomFields(event)
            is CustomFieldsEvent.AddEditCustomFields -> addEditCustomFields(event)
            is CustomFieldsEvent.ClearCustomFieldsState -> clearCustomFieldsState()
            is CustomFieldsEvent.ClearSuccessState -> clearSuccessState()
        }
    }

    fun isCustomFieldsChanged(): Boolean {
        if (state.value.type == OrganizationModel.Type.TravelAgency) {
            val ta = state.value.getTravelAgent.data ?: TravelAgentModel.empty

            val commissionType =
                if (state.value.customFields.value.commissionType == CustomFieldsModel.CommissionType.Net)
                    TravelAgentModel.CommissionType.Net else if (state.value.customFields.value.commissionType == CustomFieldsModel.CommissionType.Gross)
                    TravelAgentModel.CommissionType.Gross else
                    null

            return !((ta.commission ?: 0).toDouble() == (state.value.customFields.value.commission ?: 0).toDouble() &&
                (
                    (ta.commissionType ?: TravelAgentModel.CommissionType.Net) == (commissionType
                        ?: TravelAgentModel.CommissionType.Net)
                    )
                &&
                ta.source.orEmpty() == state.value.customFields.value.source.orEmpty()
                )
        } else if (state.value.type == OrganizationModel.Type.Supplier) {
            val supplier = state.value.getSupplier.data ?: SupplierModel.empty

            return !(supplier.category?.id == state.value.customFields.value.category?.id &&
                supplier.area?.id == state.value.customFields.value.area?.id &&
                supplier.description.orEmpty() == state.value.customFields.value.description.orEmpty()
                )
        } else {
            return false
        }
    }

    private fun setType(event: CustomFieldsEvent.SetType) =
        state.update {
            it.copy(
                type = event.type,
            )
        }.also {
            if (event.type == OrganizationModel.Type.Supplier) {
                getAllCategoryList()
                getAllAreaList()
                event.contactId?.let { contactId ->
                    getSupplier(contactId)
                }
            } else {
                event.contactId?.let { contactId ->
                    getTravelAgent(contactId)
                }
            }
        }

    private fun getSupplier(contactId: String) =
        getData(
            getData = { supplierRepository.getByContactId(contactId) },
            getState = { state.value.getSupplier },
            setState = { newState -> state.update { it.copy(getSupplier = newState) } },
            onSuccess = { supplier ->
                if (supplier != null)
                    state.value.customFields.value = state.value.customFields.value.copy(
                        category = supplier.category,
                        area = supplier.area,
                        description = supplier.description,
                    )
                else
                    state.value.customFields.value = CustomFieldsModel.empty
            },
        )

    private fun getTravelAgent(contactId: String) =
        getData(
            getData = { travelAgentRepository.getByContactId(contactId) },
            getState = { state.value.getTravelAgent },
            setState = { newState -> state.update { it.copy(getTravelAgent = newState) } },
            onSuccess = { ta ->
                if (ta != null)
                    state.value.customFields.value = state.value.customFields.value.copy(
                        source = ta.source,
                        commission = ta.commission,
                        commissionType = when (ta.commissionType) {
                            TravelAgentModel.CommissionType.Net -> CustomFieldsModel.CommissionType.Net
                            TravelAgentModel.CommissionType.Gross -> CustomFieldsModel.CommissionType.Gross
                            else -> null
                        },
                    )
                else
                    state.value.customFields.value = CustomFieldsModel.empty
            },
        )

    private fun getAllCategoryList() =
        getData(
            getData = { categoryRepository.getAll() },
            getState = { state.value.getAllCategory },
            setState = { newState -> state.update { it.copy(getAllCategory = newState) } },
        )

    private fun getAllAreaList() =
        getData(
            getData = { areaRepository.getAll() },
            getState = { state.value.getAllArea },
            setState = { newState -> state.update { it.copy(getAllArea = newState) } },
        )

    private fun updateCustomFields(event: CustomFieldsEvent.UpdateCustomFields) {
        state.value.customFields.value = event.customFields
    }

    private fun clearCustomFieldsState() =
        state.update {
            it.copy(
                type = null,
                getSupplier = State(),
                getTravelAgent = State(),
                addEditSupplier = State(),
                addEditTravelAgent = State(),
            )
        }.also {
            state.value.customFields.value = CustomFieldsModel.empty
        }

    private fun addEditCustomFields(event: CustomFieldsEvent.AddEditCustomFields) {
        if (state.value.type == OrganizationModel.Type.TravelAgency) {
            val ta = state.value.getTravelAgent.data
            if (ta != null) {
                updateTravelAgent()
            } else {
                if (!state.value.customFields.value.isEmpty)
                    insertTravelAgent(event.contactId)
            }
        } else if (state.value.type == OrganizationModel.Type.Supplier) {
            val supplier = state.value.getSupplier.data
            if (supplier != null) {
                updateSupplier()
            } else {
                if (!state.value.customFields.value.isEmpty) {
                    insertSupplier(event.contactId)
                }
            }
        }
    }

    private fun insertTravelAgent(contactId: String) =
        getData(
            getData = {
                travelAgentRepository.insert(
                    TravelAgentModel.empty.copy(
                        commission = state.value.customFields.value.commission,
                        commissionType = when (state.value.customFields.value.commissionType) {
                            CustomFieldsModel.CommissionType.Net -> TravelAgentModel.CommissionType.Net
                            CustomFieldsModel.CommissionType.Gross -> TravelAgentModel.CommissionType.Gross
                            else -> null
                        },
                        source = state.value.customFields.value.source,
                        contact = ContactModel.empty.copy(id = contactId),
                    ),
                )
            },
            getState = { state.value.addEditTravelAgent },
            setState = { newState -> state.update { it.copy(addEditTravelAgent = newState) } },
            onSuccess = { ta ->
                state.update {
                    it.copy(
                        getTravelAgent = State.success(ta),
                    )
                }
            },
        )

    private fun updateTravelAgent() {
        val ta = state.value.getTravelAgent.data ?: return

        if (!isCustomFieldsChanged()) {
            state.update {
                it.copy(
                    addEditTravelAgent = State.success(ta),
                )
            }

            return
        }

        getData(
            getData = {
                travelAgentRepository.update(
                    ta.copy(
                        commission = state.value.customFields.value.commission,
                        commissionType = when (state.value.customFields.value.commissionType) {
                            CustomFieldsModel.CommissionType.Net -> TravelAgentModel.CommissionType.Net
                            CustomFieldsModel.CommissionType.Gross -> TravelAgentModel.CommissionType.Gross
                            else -> null
                        },
                        source = state.value.customFields.value.source,
                    ),
                )
            },
            getState = { state.value.addEditTravelAgent },
            setState = { newState -> state.update { it.copy(addEditTravelAgent = newState) } },
            onSuccess = { ta ->
                state.update {
                    it.copy(
                        getTravelAgent = State.success(ta),
                    )
                }
            },
        )
    }

    private fun insertSupplier(contactId: String) =
        getData(
            getData = {
                supplierRepository.insert(
                    SupplierModel.empty.copy(
                        category = state.value.customFields.value.category,
                        area = state.value.customFields.value.area,
                        description = state.value.customFields.value.description,
                        contact = ContactModel.empty.copy(id = contactId),
                    ),
                )
            },
            getState = { state.value.addEditSupplier },
            setState = { newState -> state.update { it.copy(addEditSupplier = newState) } },
            onSuccess = { supplier ->
                state.update {
                    it.copy(
                        getSupplier = State.success(supplier),
                    )
                }
            },
        )

    private fun updateSupplier() {
        val supplier = state.value.getSupplier.data ?: return

        if (!isCustomFieldsChanged()) {
            state.update {
                it.copy(
                    addEditSupplier = State.success(supplier),
                )
            }

            return
        }

        getData(
            getData = {
                supplierRepository.update(
                    supplier.copy(
                        category = state.value.customFields.value.category,
                        area = state.value.customFields.value.area,
                        description = state.value.customFields.value.description,
                    ),
                )
            },
            getState = { state.value.addEditSupplier },
            setState = { newState -> state.update { it.copy(addEditSupplier = newState) } },
            onSuccess = { supplier ->
                state.update {
                    it.copy(
                        getSupplier = State.success(supplier),
                    )
                }
            },
        )
    }

    private fun clearSuccessState() =
        state.update {
            it.copy(
                addEditSupplier = State(),
                addEditTravelAgent = State(),
            )
        }
}